import type { Geofence, Geometry } from "@tdelivr/api-client/generated/geo/geofence.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { absent, at, field, type RecordDetail } from "./record-detail.ts";

/**
 * What the geofences board does to the rows GEO-05 returns, away from any rendering.
 *
 * The same split as `offer-monitor.ts` and `service-areas.ts`: searching, ordering, paging,
 * describing a boundary and validating an amendment can all be stated and tested without a browser,
 * and a component that does them inline can only be checked by rendering it.
 *
 * **This screen's finding is not an absent field. It is that the amendment this page exists to make
 * was impossible for everybody until GEO-05 published a read.** `PUT
 * /v1/tenants/{tenantId}/geofences/{geofenceId}` requires an `expectedVersion`, and before the
 * listing and the by-identifier read existed that number was disclosed in a write response and
 * nowhere else — so the only caller who could amend a fence was whoever had last written one, and a
 * required parameter nobody can obtain is a closed door rather than a concurrency check. Both reads
 * now publish `version` on every row, which is what makes `amendment()` below expressible at all.
 * R-19 in `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 */

/** GEO-05's published status enum, as one name. */
export type GeofenceStatus = Geofence["status"];

/**
 * Every status the listing's own `status` filter accepts, in the contract's order.
 *
 * Two, and the listing returns both when neither is asked for. That is deliberate on the service's
 * side and worth restating here: every operational board on this platform defaults to its live rows
 * because a finished delivery is noise, and an inactive geofence is not finished work. It is a
 * boundary somebody switched off and may switch back on, so defaulting to `active` would tell an
 * operator it had been deleted.
 */
export const FILTERABLE_STATUSES: readonly GeofenceStatus[] = ["active", "inactive"];

/**
 * How many fences to take in one request.
 *
 * 200 is GEO-05's own cap, stated in the parameter's own description: "defaulting to 50 and capped
 * at 200. A limit above the cap is clamped rather than refused." A tenant with more boundaries than
 * that is looking at the first 200 alphabetically, and the board says so rather than implying it
 * holds them all.
 */
export const PER_FETCH = "200";

/**
 * The state class and the word for a fence.
 *
 * `classOfStatus` cannot answer this: `packages/design-tokens`' lifecycle table is read out of
 * `01 Token System v2.dc.html` and carries no GEO-05 entry, so a badge left to the table would
 * render every row unclassified. The classes are the design's own for the two facts that do
 * correspond — `active` is `done`, which is the class the token file labels for a reached end state,
 * and `inactive` is `closed`, whose own example list is the switched-off kind. The words are the
 * contract's two values rather than a translation, because there is nothing to translate: a fence
 * is in force or it is not, and no third thing is published.
 */
export function stateOf(fence: Geofence): { classKey: StateClassKey; label: string } {
  return fence.status === "active"
    ? { classKey: "done", label: "active" }
    : { classKey: "closed", label: "inactive" };
}

/**
 * The design's third data column, from the one shape fact a row publishes.
 *
 * GEO-05 publishes a discriminated `geometry` and nothing else about the boundary's extent — no
 * area, no bounding box, no centroid name. So a circle is said as its radius and a polygon as its
 * vertex count, which are the two numbers actually on the wire.
 *
 * The closing vertex is not counted. The contract requires a polygon to repeat its first vertex to
 * close the ring — `vertices.min(4)` for a triangle — so the stored array is one longer than the
 * shape has corners, and a four-sided fence reading "5 points" would be wrong about the thing an
 * operator can see.
 */
export function shapeText(geometry: Geometry): string {
  if (geometry.type === "circle") {
    return `circle · ${geometry.radiusMeters.toLocaleString("en-GB")} m radius`;
  }
  const corners = Math.max(0, geometry.vertices.length - 1);
  return `polygon · ${corners} point${corners === 1 ? "" : "s"}`;
}

/**
 * Where the centre of a fence is, for an operator reading a list of boundaries.
 *
 * A circle publishes its centre. A polygon publishes its vertices and no centre, so the arithmetic
 * mean of the open ring is used and said to be that: it is the centroid of the *vertices* rather
 * than of the area, which differs for an irregular shape, and nothing on the platform publishes the
 * second. Shown to five decimal places, which is about a metre — more than that would imply a
 * precision the mean of a hand-drawn boundary does not have.
 */
export function centreText(geometry: Geometry): string {
  if (geometry.type === "circle") {
    return `${geometry.center.latitude.toFixed(5)}, ${geometry.center.longitude.toFixed(5)}`;
  }
  const open = geometry.vertices.slice(0, -1);
  if (open.length === 0) return "no vertices";
  const latitude = open.reduce((sum, point) => sum + point.latitude, 0) / open.length;
  const longitude = open.reduce((sum, point) => sum + point.longitude, 0) / open.length;
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type GeofenceOrder = "default" | "state" | "az" | "za";

export interface GeofenceQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  readonly order: GeofenceOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface GeofencePage {
  /** The rows on the current page. */
  readonly rows: readonly Geofence[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly Geofence[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(fence: Geofence): string {
  return [
    fence.name,
    fence.id,
    fence.status,
    fence.geometry.type,
    shapeText(fence.geometry),
    centreText(fence.geometry),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order GEO-05 answered in — `ORDER BY name, geofence_id`, alphabetical — and the
 * screen must not quietly replace it. That ordering is the service's own answer to the question a
 * configuration page asks: an operator arrives here already knowing they want the one called
 * *airport pickup*, which is not true of any operational board in this console.
 *
 * There is no status filter here. GEO-05's listing publishes `status` as a query parameter, so
 * choosing one refetches rather than hiding rows the browser already holds — otherwise a board
 * narrowed to `inactive` would be narrowing the first 200 alphabetically instead of asking the
 * service for the switched-off ones.
 */
export function geofencePage(rows: readonly Geofence[], query: GeofenceQuery): GeofencePage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0
      ? [...rows]
      : rows.filter((fence) => searchableText(fence).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          // "Name" is the first column, as the design's A–Z is: the fence's name.
          const compared = left.name.localeCompare(right.name);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an operator about what it fetched, or nothing.
 *
 * The design gives the grid no such line; the dispatch console's boards added one for the reason
 * R-08 defends — an operator working a partial board while believing it whole is the failure it
 * exists to prevent. Here it can only ever say one thing, because GEO-05's unfiltered listing is
 * genuinely every fence rather than a live subset, so there is no second half to warn about.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether GEO-05 answered a `nextAfter`, meaning this is the head of the list.
 * @param status the status the operator chose, or `undefined` for both.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  status: GeofenceStatus | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (!truncated) return undefined;
  const scope = status === undefined ? "geofences" : `${status} geofences`;
  return `More than ${perFetch} ${scope} match. This board shows the first ${perFetch} alphabetically, so the count is a floor rather than a total.`;
}

/**
 * What an operator has typed into the add or edit form.
 *
 * Strings throughout, including the numbers, because that is what an `<input>` holds — and parsing
 * at the edge rather than on every keystroke is what lets the form say "Radius must be a whole
 * number of metres" instead of silently reading a half-typed value as something else.
 */
export interface GeofenceDraft {
  readonly name: string;
  readonly shape: "circle" | "polygon";
  /** Circle only. */
  readonly latitude: string;
  readonly longitude: string;
  readonly radiusMeters: string;
  /** Polygon only: one `latitude, longitude` pair per line, as the design's boundary field takes. */
  readonly vertices: string;
  /** Edit only. A created fence is `active` — GEO-05 decides that, not this form. */
  readonly status: GeofenceStatus;
}

/** An empty draft, which is what the add form opens on. */
export const EMPTY_DRAFT: GeofenceDraft = {
  name: "",
  shape: "circle",
  latitude: "",
  longitude: "",
  radiusMeters: "",
  vertices: "",
  status: "active",
};

/**
 * A fence, as the form that edits it.
 *
 * The round trip has to be exact or an operator who changes the name moves the boundary: `PUT`
 * replaces the whole record, so every field the form does not carry is a field the amendment
 * silently rewrites. There is no partial update on this service and that is the right design — a
 * boundary is one thing — but it makes the form's job "show all of it" rather than "show what you
 * might change".
 */
export function draftOf(fence: Geofence): GeofenceDraft {
  if (fence.geometry.type === "circle") {
    return {
      name: fence.name,
      shape: "circle",
      latitude: String(fence.geometry.center.latitude),
      longitude: String(fence.geometry.center.longitude),
      radiusMeters: String(fence.geometry.radiusMeters),
      vertices: "",
      status: fence.status,
    };
  }
  return {
    name: fence.name,
    shape: "polygon",
    latitude: "",
    longitude: "",
    radiusMeters: "",
    vertices: fence.geometry.vertices
      .map((point) => `${point.latitude}, ${point.longitude}`)
      .join("\n"),
    status: fence.status,
  };
}

/** One complaint about one field, in the words the form puts under it. */
export interface DraftProblem {
  readonly field: keyof GeofenceDraft;
  readonly message: string;
}

/**
 * The geometry a draft describes, or the reasons it describes none.
 *
 * Every bound here is GEO-05's own, read from the Zod schema its route validates with and from the
 * domain rules behind it — latitude −90 to 90, longitude −180 to 180, a whole radius from 1 to
 * 1,000,000 metres, at least four vertices with the first repeated last. Restated rather than
 * imported, because a console must not import a service's source; asserted against the published
 * contract in `tests/geofences.test.ts` so the two cannot drift apart silently.
 *
 * The closing vertex is appended here rather than asked of the operator. The contract requires the
 * ring to be closed and the design's boundary field is a list of corners, so making somebody type
 * the first corner twice would be asking them to satisfy a storage convention. A draft that already
 * repeats it is left alone, so a fence edited and saved unchanged is byte-identical.
 */
export function geometryOf(draft: GeofenceDraft): Geometry | readonly DraftProblem[] {
  const problems: DraftProblem[] = [];

  if (draft.shape === "circle") {
    const latitude = finite(draft.latitude);
    const longitude = finite(draft.longitude);
    const radius = finite(draft.radiusMeters);
    if (latitude === undefined || latitude < -90 || latitude > 90) {
      problems.push({ field: "latitude", message: "Latitude must be between -90 and 90." });
    }
    if (longitude === undefined || longitude < -180 || longitude > 180) {
      problems.push({ field: "longitude", message: "Longitude must be between -180 and 180." });
    }
    if (radius === undefined || !Number.isInteger(radius) || radius < 1 || radius > 1_000_000) {
      problems.push({
        field: "radiusMeters",
        message: "Radius must be a whole number of metres from 1 to 1000000.",
      });
    }
    if (problems.length > 0) return problems;
    return {
      type: "circle",
      center: { latitude: latitude as number, longitude: longitude as number },
      radiusMeters: radius as number,
    };
  }

  const lines = draft.vertices
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const points: { latitude: number; longitude: number }[] = [];
  for (const line of lines) {
    const parts = line.split(",").map((part) => part.trim());
    const latitude = parts.length === 2 ? finite(parts[0] ?? "") : undefined;
    const longitude = parts.length === 2 ? finite(parts[1] ?? "") : undefined;
    if (
      latitude === undefined ||
      longitude === undefined ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      problems.push({
        field: "vertices",
        message: `"${line}" is not a latitude and longitude inside -90..90, -180..180.`,
      });
      return problems;
    }
    points.push({ latitude, longitude });
  }

  const first = points[0];
  const last = points.at(-1);
  const closed =
    first !== undefined &&
    last !== undefined &&
    first.latitude === last.latitude &&
    first.longitude === last.longitude;
  const corners = closed ? points.length - 1 : points.length;
  if (corners < 3) {
    problems.push({
      field: "vertices",
      message: "A polygon needs at least three corners, one per line.",
    });
    return problems;
  }
  if (points.length > 1001) {
    problems.push({ field: "vertices", message: "A polygon takes at most 1000 corners." });
    return problems;
  }
  // The ring is closed here rather than by the operator: the contract stores the closing repeat and
  // the form collects corners.
  return { type: "polygon", vertices: closed ? points : [...points, first as (typeof points)[0]] };
}

/** Whether a name satisfies GEO-05's own `string.trim().min(2).max(160)`. */
function nameProblem(name: string): DraftProblem | undefined {
  const trimmed = name.trim();
  if (trimmed.length < 2 || trimmed.length > 160) {
    return { field: "name", message: "Name must be 2 to 160 characters." };
  }
  return undefined;
}

/** The body `POST /v1/tenants/{tenantId}/geofences` takes, or why the draft cannot make one. */
export function creation(
  draft: GeofenceDraft,
): { readonly name: string; readonly geometry: Geometry } | readonly DraftProblem[] {
  const problems: DraftProblem[] = [];
  const named = nameProblem(draft.name);
  if (named !== undefined) problems.push(named);
  const geometry = geometryOf(draft);
  if (Array.isArray(geometry)) problems.push(...(geometry as readonly DraftProblem[]));
  if (problems.length > 0) return problems;
  return { name: draft.name.trim(), geometry: geometry as Geometry };
}

/**
 * The body `PUT /v1/tenants/{tenantId}/geofences/{geofenceId}` takes, or why the draft cannot make
 * one.
 *
 * **`expectedVersion` comes from the row the operator opened, and that is the whole point of this
 * function.** It is not a number the form collects, invents or defaults: it is the version the
 * screen was shown, so a fence somebody else amended in the meantime is refused with GEO-05's
 * `version_conflict` rather than silently overwritten. Before the read existed there was no
 * honest value to put here at all.
 */
export function amendment(
  draft: GeofenceDraft,
  expectedVersion: number,
):
  | {
      readonly name: string;
      readonly geometry: Geometry;
      readonly status: GeofenceStatus;
      readonly expectedVersion: number;
    }
  | readonly DraftProblem[] {
  const made = creation(draft);
  if (Array.isArray(made)) return made as readonly DraftProblem[];
  const body = made as { readonly name: string; readonly geometry: Geometry };
  return { ...body, status: draft.status, expectedVersion };
}

function finite(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * The selected fence, for the panel beside the board.
 *
 * The grid says what shape a fence is; this says where it is. A circle has a centre and a radius
 * and both are published, so both are drawn — `centreText` already formats the pair the way the
 * board does, and the radius is repeated in full here rather than rounded into the shape word.
 *
 * **A polygon gets its vertex count and no centre**, which is not an omission. GEO-05 publishes no
 * centroid, no bounding box and no area, and a centre computed here would be this console
 * inventing a position for a shape an operator is about to dispatch against. `shapeText`'s own
 * comment records why the closing vertex is not counted, and this panel counts the same way so the
 * two cannot disagree.
 */
export function geofenceDetail(fence: Geofence): RecordDetail {
  const state = stateOf(fence);
  const circle = fence.geometry.type === "circle";

  return {
    headline: fence.name,
    identifier: fence.id,
    source: "GEO-05",
    state: { ...state, code: "GEO-05", status: fence.status },
    fields: [
      field("Shape", shapeText(fence.geometry)),
      ...(circle
        ? [
            field("Centre", centreText(fence.geometry), true),
            field(
              "Radius",
              `${String(fence.geometry.type === "circle" ? fence.geometry.radiusMeters : 0)} m`,
            ),
          ]
        : [
            absent(
              "Centre",
              "GEO-05 publishes a polygon's vertices and no centroid, bounding box or area. A centre computed here would be a position this console invented.",
            ),
          ]),
      field("Created", at(fence.createdAt) ?? fence.createdAt),
      field("Last changed", at(fence.updatedAt) ?? fence.updatedAt),
      field("Version", `v${String(fence.version)}`),
    ],
    // The key is omitted rather than set to undefined: `exactOptionalPropertyTypes` treats the two
    // as different, and it is right to — a note explicitly set to nothing is not the same statement
    // as a row that has no note.
    ...(fence.status === "inactive"
      ? {
          note: "An inactive fence is still published and still readable. GEO-05 has no delete, so this is how a fence is retired.",
        }
      : {}),
  };
}
